import React from "react";
import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Image as ImageIcon, MessageCircle, Instagram, Mail, Globe, Phone, Tag, ListTodo, Mic2, Paperclip } from "lucide-react";
import { motion } from "framer-motion";
import { absoluteMediaUrl } from "../../lib/api";
import { cleanTechnicalMediaText, normalizeMediaType } from "../../utils/formatMessage";

const CHANNEL_ICONS = {
  whatsapp: { icon: MessageCircle, color: "text-[#25D366]", bg: "bg-[#25D366]/10" },
  instagram: { icon: Instagram, color: "text-pink-400", bg: "bg-pink-500/10" },
  facebook: { icon: Globe, color: "text-blue-400", bg: "bg-blue-500/10" },
  email: { icon: Mail, color: "text-cyan-400", bg: "bg-cyan-500/10" },
  webchat: { icon: Globe, color: "text-purple-400", bg: "bg-purple-500/10" },
  manual: { icon: Phone, color: "text-slate-400", bg: "bg-slate-500/10" },
};

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}

function getInitials(name = "") {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return "?";

  if (parts.length === 1) {
    return parts[0][0].toUpperCase();
  }

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}
export default function KanbanCard({ lead, onClick, index }) {
  const ch = CHANNEL_ICONS[lead.channel] || CHANNEL_ICONS.manual;
  const ChIcon = ch.icon;
  const pendingTasks = (lead.tasks || []).filter((task) => !task.completed);
  const lastMediaType = normalizeMediaType(
    lead.last_message_media_type || lead.last_media_type || lead.media_type,
    lead.last_message_preview
  );
  const lastMediaUrl = lead.last_message_media_url || lead.last_media_url || lead.media_url;
  const lastMediaPreview = lastMediaUrl ? absoluteMediaUrl(lastMediaUrl) : null;
  const previewText = cleanTechnicalMediaText(lead.last_message_preview, lastMediaType);
  const displayPhone = lead.display_phone || null;
  const customerPhoneLabel = displayPhone || "Telefone não informado";

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: lead.id,
    data: { lead },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <motion.div
      ref={setNodeRef}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, delay: Math.min(index * 0.03, 0.3) }}
      style={style}
      {...attributes}
      {...listeners}
      onClick={(e) => {
        if (isDragging) return;
        e.stopPropagation();
        onClick?.(lead);
      }}
      data-testid={`kanban-card-${lead.id}`}
      className={`group bg-slate-800 border border-slate-700 rounded-lg p-3.5 cursor-grab active:cursor-grabbing hover:-translate-y-0.5 hover:border-slate-500 transition-all shadow-sm ${
        isDragging ? "kanban-card-dragging" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center text-xs font-bold text-slate-100 shrink-0">
            {getInitials(lead.name)}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-100 truncate">{lead.name}</div>
            <div className={`text-[11px] truncate ${displayPhone ? "text-slate-500" : "text-slate-600 italic"}`}>
              {customerPhoneLabel}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {pendingTasks.length > 0 && (
            <span className="inline-flex items-center gap-1 px-1.5 py-1 rounded-md text-[10px] font-semibold bg-amber-500/10 border border-amber-500/20 text-amber-300">
              <ListTodo className="w-3 h-3" />
              {pendingTasks.length}
            </span>
          )}
          <div className={`w-7 h-7 rounded-md ${ch.bg} flex items-center justify-center shrink-0`}>
            <ChIcon className={`w-3.5 h-3.5 ${ch.color}`} />
          </div>
        </div>
      </div>

      {(previewText || lastMediaType) && (
        <div className="mb-2.5 pl-0.5">
          {lastMediaType === "image" && lastMediaPreview ? (
            <div className="flex items-center gap-2">
              <img
                src={lastMediaPreview}
                alt="Imagem"
                className="h-9 w-9 rounded-md object-cover"
                loading="lazy"
              />
              <span className="min-w-0 truncate text-xs text-slate-400">
                {previewText || "Imagem"}
              </span>
            </div>
          ) : lastMediaType === "document" ? (
            <div className="flex items-center gap-1.5 text-xs text-slate-400">
              <Paperclip className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{previewText || "Documento"}</span>
            </div>
          ) : lastMediaType === "audio" ? (
            <div className="flex items-center gap-1.5 text-xs text-slate-400">
              <Mic2 className="h-3.5 w-3.5 shrink-0" />
              <span>Áudio</span>
            </div>
          ) : lastMediaType === "image" ? (
            <div className="flex items-center gap-1.5 text-xs text-slate-400">
              <ImageIcon className="h-3.5 w-3.5 shrink-0" />
              <span>Imagem</span>
            </div>
          ) : (
            <p className="line-clamp-2 text-xs leading-snug text-slate-400">
              {previewText}
            </p>
          )}
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1 flex-wrap">
          {(lead.tags || []).slice(0, 2).map((t) => (
            <span key={t} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-slate-700/80 text-slate-300 border border-slate-600">
              <Tag className="w-2.5 h-2.5" />
              {t}
            </span>
          ))}
          {lead.tags && lead.tags.length > 2 && (
            <span className="text-[10px] text-slate-500 font-medium">+{lead.tags.length - 2}</span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {lead.value > 0 && (
            <span className="text-[11px] font-bold text-[#25D366]">
              R$ {lead.value.toLocaleString("pt-BR")}
            </span>
          )}
          <span className="text-[10px] text-slate-500">{formatTime(lead.last_message_at || lead.updated_at)}</span>
        </div>
      </div>

      {lead.assigned_to_name && (
        <div className="mt-2 pt-2 border-t border-slate-700/50 flex items-center gap-1.5">
          <div className="w-4 h-4 rounded-full bg-indigo-600 flex items-center justify-center text-[8px] font-bold text-white shrink-0">
            {getInitials(lead.assigned_to_name)}
          </div>
          <span className="text-[10px] text-slate-400 truncate">{lead.assigned_to_name}</span>
        </div>
      )}
    </motion.div>
  );
}
